import CustomField from "components/Common/CustomField"
import CustomLabel from "components/Common/CustomLabel"
import { ErrorMessage, Field, Form, Formik } from "formik"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button, ModalFooter } from "reactstrap"
import { showAlert } from "store/reducers/alertReducer"
import { updateBankDetails } from "store/actions/authAction"
import { useDispatch, useSelector } from "react-redux"
import * as Yup from "yup"
import { getLocalizedName } from "components/Common/Utils"

const initialValues = {
  bank_id: "",
  bank_iban: "",
}

const UpdateBankDetails = ({
  userDetails,
  dropdownData,
  toggleModal,
  getDetails,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const dispatch = useDispatch()
  const [formValues, setFormValues] = useState(initialValues)
  const { isLoading } = useSelector(state => state.Login)

  const validationSchema = Yup.object().shape({
    bank_id: Yup.string().required(t("Field is required")),
    bank_iban: Yup.string()
      .required(t("Field is required"))
      .length(24, t("IBAN Number must be exactly 24 characters"))
      .matches(
        /^SA[0-9٠-٩]{22}$/,
        t(
          "Invalid Saudi IBAN format. It must start with 'SA' followed by 22 digits"
        )
      ),
  })

  useEffect(() => {
    if (userDetails) {
      const { bank_id, bank_iban } = userDetails?.job_application || {}
      setFormValues(prev => ({
        bank_id,
        bank_iban,
      }))
    }
  }, [userDetails])

  const handleOnSubmit = values => {
    const data = {
      uuid: userDetails?.user_information?.uuid,
      payload: values,
    }
    dispatch(updateBankDetails(data))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          getDetails()
          toggleModal()
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <div>
      <Formik
        initialValues={formValues}
        validationSchema={validationSchema}
        onSubmit={handleOnSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue }) => {
          const prevBankIdRef = useRef(values.bank_id)

          useEffect(() => {
            if (prevBankIdRef.current && prevBankIdRef.current !== values.bank_id) {
              setFieldValue("bank_iban", "")
            }
            prevBankIdRef.current = values.bank_id
          }, [values.bank_id, setFieldValue])

          return (
          <Form>
            <div className="row">
              <div className="col-lg-6">
                <div className="form_field">
                  <CustomLabel
                    htmlFor="bank_id"
                    label={t("Bank Name")}
                    isRequired={true}
                  />
                  <div className="position-relative">
                    <Field
                      as="select"
                      name="bank_id"
                      className={`form-control ${
                        errors?.bank_id && touched?.bank_id ? "is-invalid" : ""
                      }`}
                    >
                      <option hidden label={t("Select")} />
                      {dropdownData?.banks?.map(item => (
                        <option
                          value={item?.id}
                          key={item?.id}
                          label={getLocalizedName(item, language)}
                        />
                      ))}
                    </Field>
                  </div>
                  <ErrorMessage
                    name={"bank_id"}
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="form_field">
                  <CustomLabel
                    htmlFor="bank_iban"
                    label={t("IBAN Number")}
                    isRequired={true}
                  />

                  <CustomField
                    name="bank_iban"
                    placeholder={
                      language === "en"
                        ? "SA-- ---- ---- ---- ---- ----"
                        : "---- ---- ---- ---- ---- --SA"
                    }
                    touched={touched}
                    errors={errors}
                  />
                </div>
              </div>
              {/*  */}
              <div className="col-lg-12">
                <ModalFooter>
                  <Button color="secondary" type="button" onClick={toggleModal}>
                    {t("Close")}
                  </Button>
                  <Button color="success" type="submit" disabled={isLoading}>
                    {t("Update")}
                  </Button>
                </ModalFooter>
              </div>
            </div>
          </Form>
        )}
        }
      </Formik>
    </div>
  )
}

export default UpdateBankDetails
